import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../api.service';
import { CookieService } from 'ngx-cookie-service';
import { CommonService } from '../../shared/common.service';
import { HttpParams } from '@angular/common/http';

interface AuditTrailRecord {
  audit_id?: number;
  study_id?: string;
  subject_id?: string;
  visit_name?: string;
  form_name?: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  action: string;
  reason_for_change?: string;
  user_id: string;
  user_role: string;
  timestamp: string;
  user_name?: string;
}

@Component({
  selector: 'app-system-audit-trail',
  templateUrl: './system-audit-trail.component.html',
  styleUrls: ['./system-audit-trail.component.scss'],
})
export class SystemAuditTrailComponent implements OnInit {
  auditTrailData: AuditTrailRecord[] = [];
  filteredData: AuditTrailRecord[] = [];
  paginatedData: AuditTrailRecord[] = [];
  loading: boolean = false;
  
  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;
  totalRecords = 0;
  pageNumbersToShow: number = 4;
  pageRange: number[] = [];

  // Filters
  selectedUserId: string = '';
  selectedAction: string = '';
  selectedFormName: string = '';
  startDate: string = '';
  endDate: string = '';
  
  // User dropdown
  availableUsers: any[] = [];
  filteredUsers: any[] = [];
  isUserDropdownOpen: boolean = false;
  userSearchText: string = '';

  // Action dropdown
  availableActions: string[] = ['ENTERED', 'MODIFIED', 'DELETED'];
  isActionDropdownOpen: boolean = false;

  // Form dropdown
  availableForms: string[] = [];
  filteredForms: string[] = [];
  isFormDropdownOpen: boolean = false;
  formSearchText: string = '';

  authToken: string | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private cookieService: CookieService,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    let userData = this.commonService.getUserData();

    // Check if user is authenticated
    if (userData == null) {
      this.commonService.navigateTo('/');
      return;
    }

    // Check if user is CRO (CRO, CRO_Standard, CRO_Premium, or CRO_Enterprise)
    const designation = userData.designation;
    const isCRO = designation === 'CRO' || 
                  designation === 'CRO_Standard' || 
                  designation === 'CRO_Premium' || 
                  designation === 'CRO_Enterprise';
    
    if (!isCRO) {
      alert('Access denied. System audit trail is only available to CRO users.');
      this.commonService.navigateTo('/home');
      return;
    }

    this.authToken = this.cookieService.get('token');

    // Load initial data from query params
    this.route.queryParams.subscribe((params) => {
      this.selectedUserId = params['userId'] || '';
      this.selectedAction = params['action'] || '';
      this.selectedFormName = params['formName'] || '';
      this.startDate = params['startDate'] || '';
      this.endDate = params['endDate'] || '';
      this.currentPage = +params['page'] || 1;

      this.loadAuditTrail();
    });
  }

  loadUsersFromData(): void {
    // Extract unique users from audit trail data
    const uniqueUsersMap = new Map();
    this.auditTrailData.forEach((record) => {
      if (record.user_id) {
        if (!uniqueUsersMap.has(record.user_id)) {
          uniqueUsersMap.set(record.user_id, {
            id: record.user_id,
            user_id: record.user_id,
            name: record.user_name || record.user_id,
            email: ''
          });
        }
      }
    });
    this.availableUsers = Array.from(uniqueUsersMap.values()).sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    );
    this.filteredUsers = this.availableUsers;
  }

  toggleUserDropdown(): void {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
    if (this.isUserDropdownOpen) {
      this.filterUsers();
    }
  }

  filterUsers(): void {
    if (!this.userSearchText) {
      this.filteredUsers = this.availableUsers;
      return;
    }
    const searchLower = this.userSearchText.toLowerCase();
    this.filteredUsers = this.availableUsers.filter(
      (user) =>
        (user.name || user.email || '')
          .toLowerCase()
          .includes(searchLower) ||
        (user.email || '')
          .toLowerCase()
          .includes(searchLower)
    );
  }

  selectUser(user: any): void {
    this.selectedUserId = user.id || user.user_id;
    this.isUserDropdownOpen = false;
    this.userSearchText = '';
    this.loadAuditTrail();
  }

  clearUserFilter(): void {
    this.selectedUserId = '';
    this.loadAuditTrail();
  }

  toggleActionDropdown(): void {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;
  }

  selectAction(action: string): void {
    this.selectedAction = action;
    this.isActionDropdownOpen = false;
    this.loadAuditTrail();
  }

  clearActionFilter(): void {
    this.selectedAction = '';
    this.loadAuditTrail();
  }

  toggleFormDropdown(): void {
    this.isFormDropdownOpen = !this.isFormDropdownOpen;
    if (this.isFormDropdownOpen) {
      this.filterForms();
    }
  }

  filterForms(): void {
    if (!this.formSearchText) {
      this.filteredForms = this.availableForms;
      return;
    }
    const searchLower = this.formSearchText.toLowerCase();
    this.filteredForms = this.availableForms.filter((form) =>
      form.toLowerCase().includes(searchLower)
    );
  }

  selectForm(form: string): void {
    this.selectedFormName = form;
    this.isFormDropdownOpen = false;
    this.formSearchText = '';
    this.loadAuditTrail();
  }

  clearFormFilter(): void {
    this.selectedFormName = '';
    this.loadAuditTrail();
  }

  loadFormsFromData(): void {
    // Extract unique forms from audit trail data
    const uniqueForms = new Set<string>();
    this.auditTrailData.forEach((record) => {
      if (record.form_name) {
        uniqueForms.add(record.form_name);
      }
    });
    this.availableForms = Array.from(uniqueForms).sort();
    this.filteredForms = this.availableForms;
  }

  loadAuditTrail(): void {
    this.loading = true;

    const params = new HttpParams()
      .set('page', this.currentPage.toString())
      .set('limit', this.pageSize.toString())
      .set('user_id', this.selectedUserId ? this.selectedUserId : '')
      .set('action', this.selectedAction ? this.selectedAction : '')
      .set('form_name', this.selectedFormName ? this.selectedFormName : '')
      .set('start_date', this.startDate ? this.startDate : '')
      .set('end_date', this.endDate ? this.endDate : '');

    this.apiService.getSystemAuditTrail(params).subscribe(
      (response: any) => {
        if (response.success && response.data) {
          this.auditTrailData = response.data;
          this.filteredData = this.auditTrailData;
          this.totalRecords = response.pagination?.totalRecords || 0;
          this.totalPages = response.pagination?.totalPages || 0;

          // Update filter options
          this.loadFormsFromData();
          this.loadUsersFromData();

          // Backend already handles pagination, so use data directly
          this.paginatedData = this.auditTrailData;
          this.updatePageRange();
        } else {
          this.auditTrailData = [];
          this.filteredData = [];
          this.totalRecords = 0;
          this.totalPages = 0;
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching system audit trail:', error);
        this.loading = false;
        alert('Error loading system audit trail data. Please try again.');
      }
    );
  }

  updatePaginatedData(): void {
    // Backend already handles pagination, so use data directly
    this.paginatedData = this.auditTrailData;
  }

  updatePageRange(): void {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const maxPagesToShow = this.pageNumbersToShow;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxPagesToShow / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    this.pageRange = [];
    for (let i = startPage; i <= endPage; i++) {
      this.pageRange.push(i);
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadAuditTrail();
      this.updateQueryParams();
    }
  }

  updateQueryParams(): void {
    const queryParams: any = {
      page: this.currentPage,
    };

    if (this.selectedUserId) queryParams.userId = this.selectedUserId;
    if (this.selectedAction) queryParams.action = this.selectedAction;
    if (this.selectedFormName) queryParams.formName = this.selectedFormName;
    if (this.startDate) queryParams.startDate = this.startDate;
    if (this.endDate) queryParams.endDate = this.endDate;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadAuditTrail();
    this.updateQueryParams();
  }

  clearFilters(): void {
    this.selectedUserId = '';
    this.selectedAction = '';
    this.selectedFormName = '';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.loadAuditTrail();
    this.updateQueryParams();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  }

  getActionBadgeClass(action: string): string {
    switch (action?.toUpperCase()) {
      case 'ENTERED':
      case 'INSERT': // Legacy support
        return 'badge-success';
      case 'MODIFIED':
      case 'UPDATE': // Legacy support
        return 'badge-warning';
      case 'DELETED':
      case 'DELETE': // Legacy support
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  getDisplayAction(action: string): string {
    if (!action) return 'N/A';
    const actionUpper = action.toUpperCase();
    // Map old action names to new ones for consistent display
    switch (actionUpper) {
      case 'INSERT':
        return 'ENTERED';
      case 'UPDATE':
        return 'MODIFIED';
      case 'DELETE':
        return 'DELETED';
      default:
        return action; // Return as-is if already new format or unknown
    }
  }

  downloadCSV(): void {
    if (this.filteredData.length === 0) {
      alert(
        'No data available to download. Please ensure data is loaded.'
      );
      return;
    }

    try {
      // Prepare CSV header
      const headers = [
        'Audit ID',
        'Timestamp',
        'User ID',
        'User Name',
        'User Role',
        'Action',
        'Form Name',
        'Field Name',
        'Old Value',
        'New Value',
        'Reason for Change',
      ];

      // Prepare CSV rows
      const rows = this.filteredData.map((item) => [
        item.audit_id || 'N/A',
        this.formatDate(item.timestamp),
        item.user_id || 'N/A',
        item.user_name || 'N/A',
        item.user_role || 'N/A',
        item.action || 'N/A',
        item.form_name || 'N/A',
        item.field_name || 'N/A',
        item.old_value || 'N/A',
        item.new_value || 'N/A',
        item.reason_for_change || 'N/A',
      ]);

      // Combine header and rows
      const csvContent = [headers, ...rows]
        .map((e) => e.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `System_Audit_Trail_${new Date().toISOString().split('T')[0]}.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV file. Please try again.');
    }
  }

  downloadExcel(): void {
    if (this.filteredData.length === 0) {
      alert(
        'No data available to download. Please ensure data is loaded.'
      );
      return;
    }

    // For Excel, we'll use CSV format with .xlsx extension
    // In a production environment, you might want to use a library like xlsx
    this.downloadCSV();
  }

  getEndRecordNumber(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  getSelectedUserName(): string {
    if (!this.selectedUserId) return '';
    const user = this.availableUsers.find((u) => u.id === this.selectedUserId || u.user_id === this.selectedUserId);
    return user ? (user.name || user.email || this.selectedUserId) : this.selectedUserId;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.isUserDropdownOpen = false;
      this.isActionDropdownOpen = false;
      this.isFormDropdownOpen = false;
    }
  }
}

